/**
 * Typed API functions for the /api/v1/notifications/* endpoints (M4.4).
 *
 * All calls go through apiClient (axios singleton with Bearer interceptor).
 * Architecture invariant: never inline axios calls in hooks or components.
 *
 * Types are manually ported from backend/app/schemas/notification.py (M4.4)
 * pending openapi-typescript generation on the next sync-types run.
 */

import { apiClient } from '@/lib/api/client';

// ── Domain literals ───────────────────────────────────────────────────────────

export type NotificationKind = 'info' | 'warning' | 'action_required' | 'success';

// ── Response types ────────────────────────────────────────────────────────────

export interface NotificationListItem {
  id: string;
  kind: NotificationKind;
  title: string;
  body: string;
  link?: string | null;
  triggered_by_event?: string | null;
  triggered_by_entity_type?: string | null;
  triggered_by_entity_id?: string | null;
  read_at?: string | null;
  created_at: string;
}

export interface NotificationListResponse {
  items: NotificationListItem[];
  total: number;
  unread_count: number;
}

export interface NotificationListQuery {
  kind?: NotificationKind;
  unread_only?: boolean;
  page?: number;
  page_size?: number;
}

export interface MarkReadRequest {
  notification_ids: string[];
}

export interface MarkReadResponse {
  marked_count: number;
  skipped: { notification_id: string; reason: string }[];
}

export interface UnreadCountResponse {
  unread_count: number;
}

export interface NotificationPreferenceResponse {
  user_id: string;
  muted_events: string[];
  email_disabled: boolean;
  quiet_hours_start?: number | null;
  quiet_hours_end?: number | null;
  updated_at: string;
}

export interface NotificationPreferenceUpdateRequest {
  muted_events?: string[];
  email_disabled?: boolean;
  quiet_hours_start?: number | null;
  quiet_hours_end?: number | null;
}

// ── API functions ─────────────────────────────────────────────────────────────

/**
 * GET /api/v1/notifications
 * List notifications for the current user with optional kind / unread-only filter
 * and pagination.
 */
export async function listNotifications(
  query: Partial<NotificationListQuery>
): Promise<NotificationListResponse> {
  const { data } = await apiClient.get<NotificationListResponse>('/notifications', {
    params: query,
  });
  return data;
}

/**
 * GET /api/v1/notifications/unread-count
 * Lightweight endpoint used by the topbar bell badge — avoids fetching full
 * notification payloads on every poll cycle.
 */
export async function getUnreadCount(): Promise<UnreadCountResponse> {
  const { data } = await apiClient.get<UnreadCountResponse>('/notifications/unread-count');
  return data;
}

/**
 * POST /api/v1/notifications/mark-read
 * Mark one or more notifications as read. PESSIMISTIC — the server validates
 * ownership and returns skipped IDs with reasons.
 */
export async function markRead(body: MarkReadRequest): Promise<MarkReadResponse> {
  const { data } = await apiClient.post<MarkReadResponse>('/notifications/mark-read', body);
  return data;
}

/**
 * GET /api/v1/notifications/preferences
 * Fetch the current user's notification preference record.
 */
export async function getPreferences(): Promise<NotificationPreferenceResponse> {
  const { data } = await apiClient.get<NotificationPreferenceResponse>(
    '/notifications/preferences'
  );
  return data;
}

/**
 * PATCH /api/v1/notifications/preferences
 * Update notification preferences (muted events, email toggle, quiet hours).
 * PESSIMISTIC — all fields optional; the server merges with existing.
 */
export async function updatePreferences(
  body: NotificationPreferenceUpdateRequest
): Promise<NotificationPreferenceResponse> {
  const { data } = await apiClient.patch<NotificationPreferenceResponse>(
    '/notifications/preferences',
    body
  );
  return data;
}
